import { apiClient } from '@/lib/api';

export interface KybRequirement {
  id: string;
  type: 'DOCUMENT' | 'QUESTION';
  label: string;
  description?: string;
  required: boolean;
  acceptedFormats?: string;
  maxSizeMB?: number;
  answerType?: 'TEXT' | 'NUMBER' | 'SELECT' | 'TEXTAREA';
  options?: string[];
  order: number;
  isActive: boolean;
}

export interface KybDocumentEntry {
  requirementId?: string;
  type: string;
  url?: string;
  fileName?: string;
  fileSize?: number;
  answer?: string;
  status?: string;
  superseded?: boolean;
  revision?: number;
  reviewNotes?: string;
  uploadedAt?: string;
}

export interface KybSubmissionItem {
  requirementId: string;
  type: string;
  fileName?: string;
  fileUrl?: string;
  answer?: string;
}

export interface KybDraft {
  items: KybSubmissionItem[];
  savedAt: string;
}

export interface KybSubmissionData {
  kybData: { documents?: KybDocumentEntry[]; draft?: KybDraft } | null;
  kybStatus: string;
}

export interface KybReviewItem {
  id: string;
  tenantName: string;
  country: string;
  adminEmail: string;
  status: string;
  submittedAt: string | null;
  reviewedBy: string | null;
  itemCount: number;
}

export const kybService = {

  async uploadKybFile(file: File): Promise<{ url: string; fileName: string; fileSize: number; mimeType: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post<{ url: string; fileName: string; fileSize: number; mimeType: string }>('/tenant-config/kyb/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  // Reviews (Super Admin)

  async getKybReviews(): Promise<KybReviewItem[]> {
    const { data } = await apiClient.get<KybReviewItem[]>('/tenant-config/kyb-reviews');
    return data;
  },

  // Requirements (Super Admin)

  async getRequirements(activeOnly = true): Promise<KybRequirement[]> {
    const { data } = await apiClient.get<KybRequirement[]>('/tenant-config/kyb-requirements', {
      params: { activeOnly },
    });
    return data;
  },

  async createRequirement(payload: Partial<KybRequirement>): Promise<KybRequirement> {
    const { data } = await apiClient.post<KybRequirement>('/tenant-config/kyb-requirements', payload);
    return data;
  },

  async updateRequirement(id: string, payload: Partial<KybRequirement>): Promise<KybRequirement> {
    const { data } = await apiClient.put<KybRequirement>(`/tenant-config/kyb-requirements/${id}`, payload);
    return data;
  },

  async deleteRequirement(id: string): Promise<void> {
    await apiClient.delete(`/tenant-config/kyb-requirements/${id}`);
  },

  async reorderRequirement(id: string, order: number): Promise<KybRequirement> {
    const { data } = await apiClient.patch<KybRequirement>(`/tenant-config/kyb-requirements/${id}/reorder`, { order });
    return data;
  },

  // Submissions (IT Admin)

  async getTenantKyb(tenantId: string): Promise<KybSubmissionData> {
    const { data } = await apiClient.get<KybSubmissionData>(`/tenant-config/tenants/${tenantId}/kyb`);
    return data;
  },

  async submitKybForReview(tenantId: string, items: KybSubmissionItem[]): Promise<KybSubmissionData> {
    const { data } = await apiClient.post<KybSubmissionData>(`/tenant-config/tenants/${tenantId}/kyb/submit`, { items });
    return data;
  },

  async saveDraft(tenantId: string, items: KybSubmissionItem[]): Promise<KybSubmissionData> {
    const { data } = await apiClient.put<KybSubmissionData>(`/tenant-config/tenants/${tenantId}/kyb/draft`, { items });
    return data;
  },

  async submitDocument(tenantId: string, document: { type: string; url: string; requirementId?: string; uploadNotes?: string }): Promise<KybSubmissionData> {
    const { data } = await apiClient.post<KybSubmissionData>(`/tenant-config/tenants/${tenantId}/kyb/document`, document);
    return data;
  },

  // Review (Super Admin)

  async reviewDocument(tenantId: string, documentIndex: number, status: 'approved' | 'rejected', reviewNotes?: string): Promise<KybSubmissionData> {
    const { data } = await apiClient.patch<KybSubmissionData>(`/tenant-config/tenants/${tenantId}/kyb/review/${documentIndex}`, { status, reviewNotes });
    return data;
  },
};
